/**
 * Payments Page
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuthStore } from '../store/authStore';
import { formatCurrency, formatDateTime } from '../utils/formatters';
import './Payments.css';

const Payments = () => {
  const user = useAuthStore((state) => state.user);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    if (!user || !db) return;

    const q = query(
      collection(db, 'payments'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const paymentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayments(paymentsData);
    });

    return () => unsubscribe();
  }, [user]);

  const getStatusBadge = (status) => {
    const statusMap = {
      succeeded: <span className="badge badge-success">Succeeded</span>,
      pending: <span className="badge badge-warning">Pending</span>,
      failed: <span className="badge badge-danger">Failed</span>,
      refunded: <span className="badge badge-info">Refunded</span>,
    };
    return statusMap[status] || <span className="badge">{status}</span>;
  };

  const totalReceived = payments
    .filter(p => p.status === 'succeeded')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Payments</h1>
          <p className="page-subtitle">
            View payment history and transactions
          </p>
        </div>
      </div>

      <div className="payment-summary">
        <div className="summary-card">
          <div className="summary-label">Total Received</div>
          <div className="summary-value">{formatCurrency(totalReceived)}</div>
          <div className="summary-meta">{payments.filter(p => p.status === 'succeeded').length} transactions</div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="empty-state">
          <p>No payment history yet. Payments will appear here once invoices are paid.</p>
        </div>
      ) : (
        <div className="payment-list">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Gateway</th>
                <th>Status</th>
                <th>Transaction ID</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{formatDateTime(payment.createdAt)}</td>
                  <td>
                    <a href={`/invoices`} className="link">
                      Invoice #{payment.invoiceId?.slice(-8)}
                    </a>
                  </td>
                  <td className="font-bold">
                    {formatCurrency(payment.amount, payment.currency)}
                  </td>
                  <td>
                    {payment.paymentMethodType === 'card' && '💳 Card'}
                    {payment.paymentMethodType === 'wallet' && '👛 Wallet'}
                    {payment.paymentMethodType === 'bank_transfer' && '🏦 Bank'}
                  </td>
                  <td>
                    <span className="gateway-badge">
                      {payment.paymentGateway || 'stripe'}
                    </span>
                  </td>
                  <td>{getStatusBadge(payment.status)}</td>
                  <td className="font-mono text-small">
                    {payment.gatewayTransactionId?.slice(0, 16)}...
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Payments;
